// Estimated delivery dates (blocks/flow-delivery-estimate.liquid), computed from the visitor's clock because
// pages are cached. Business days skip the closed weekdays and dates; past the cutoff hour, shipping moves to the
// next business day, then the minimum and maximum delays are counted in business days from that shipping day.
class FlowDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  closedDates() {
    return (this.dataset.closedDates || '')
      .split(/[\s,;]+/)
      .map((value) => value.trim())
      .filter((value) => /^(\d{4}-)?\d{2}-\d{2}$/.test(value));
  }

  isBusinessDay(date, closedDates) {
    const weekday = date.getDay();
    const closed = this.dataset.closedWeekdays;
    if (weekday === 0 && (closed === 'weekend' || closed === 'sunday')) return false;
    if (weekday === 6 && closed === 'weekend') return false;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const monthDay = `${month}-${day}`;
    return !closedDates.includes(monthDay) && !closedDates.includes(`${date.getFullYear()}-${monthDay}`);
  }

  addBusinessDays(from, count, closedDates) {
    const date = new Date(from);
    let remaining = count;
    // A guard keeps a misconfigured calendar (every day closed) from looping forever.
    for (let guard = 0; remaining > 0 && guard < 400; guard += 1) {
      date.setDate(date.getDate() + 1);
      if (this.isBusinessDay(date, closedDates)) remaining -= 1;
    }
    return date;
  }

  estimate(now = new Date()) {
    const closedDates = this.closedDates();
    const cutoff = Number(this.dataset.cutoff ?? 24);
    let shipping = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (!this.isBusinessDay(shipping, closedDates) || now.getHours() >= cutoff) {
      shipping = this.addBusinessDays(shipping, 1, closedDates);
    }
    const min = Number(this.dataset.minDays || 0);
    const max = Number(this.dataset.maxDays || 0);
    return [
      this.addBusinessDays(shipping, Math.min(min, max), closedDates),
      this.addBusinessDays(shipping, Math.max(min, max), closedDates),
    ];
  }

  format(date) {
    const options = {
      numeric: { day: '2-digit', month: '2-digit' },
      short: { day: 'numeric', month: 'short' },
      long: { day: 'numeric', month: 'long' },
      weekday: { weekday: 'long', day: 'numeric', month: 'long' },
    }[this.dataset.format] || { day: 'numeric', month: 'long' };
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, options).format(date);
  }

  render() {
    const target = this.querySelector('[data-delivery-text]');
    if (!target) return;
    const [start, end] = this.estimate();
    const values = { '[start]': this.format(start), '[end]': this.format(end) };
    const parts = (this.dataset.text || '').split(/(\[start\]|\[end\])/);
    target.replaceChildren(...parts.filter(Boolean).map((part) => {
      if (!(part in values)) return document.createTextNode(part);
      const date = document.createElement('strong');
      date.className = 'flow-delivery-estimate__date';
      date.textContent = values[part];
      return date;
    }));
  }
}

if (!customElements.get('flow-delivery-estimate')) customElements.define('flow-delivery-estimate', FlowDeliveryEstimate);
